import { z } from 'zod';

/**
 * Hall of Fame Grow uP (uP Awards): os premios que a associacao da aos seus
 * membros. Ver docs/hall-of-fame.
 *
 * Desde 20-10-2026 (pedido pelo GrowuP) e uma lista simples, como os Premios
 * recebidos: cada linha e um premio dado a uma pessoa, com ano, nome do
 * premio, desenho da medalha, vencedor, frase e foto opcionais. Um premio
 * com dois vencedores sao duas linhas com o mesmo ano e nome.
 *
 * Prefixos HTTP:
 *   - `/api/hall-of-fame`                   site publico (e "este premio e meu");
 *   - `/api/site/hall-of-fame/...`          back-office (`content.manage`), so em gestao.
 *
 * Os premios sao publicos: o site mostra o nome de cada vencedor. A excecao
 * sao os menores (RN-H2): enquanto o vencedor ligado a uma conta for menor, o
 * site mostra so o nickname.
 */

/** Uma imagem carregada (`/api/media/...`); nada de imagens de terceiros. */
const mediaSrcSchema = z
  .string()
  .trim()
  .regex(/^\/api\/media\/[0-9a-f-]{36}\/(sm|md|lg)\.webp$/, 'carregue a imagem no back-office');

/** O desenho de cada medalha. Lista fechada: o site so sabe desenhar estes. */
export const HOF_ICONS = [
  'medalha',
  'trofeu',
  'estrela',
  'coracao',
  'comando',
  'microfone',
  'camara',
  'escudo',
  'raio',
  'coroa',
] as const;
export const hofIconSchema = z.enum(HOF_ICONS);
export type HofIcon = z.infer<typeof hofIconSchema>;

export const HOF_ICON_LABELS: Record<HofIcon, string> = {
  medalha: 'Medalha',
  trofeu: 'Troféu',
  estrela: 'Estrela',
  coracao: 'Coração',
  comando: 'Comando',
  microfone: 'Microfone',
  camara: 'Câmara',
  escudo: 'Escudo',
  raio: 'Raio',
  coroa: 'Coroa',
};

/** O ano como aparece no site: "2025" ou uma epoca "2020-21". */
export const hofSeasonSchema = z
  .string()
  .trim()
  .regex(/^\d{4}(-\d{2})?$/, 'escreva o ano (2025) ou a época (2020-21)')
  .refine((v) => {
    const first = Number(v.slice(0, 4));
    if (first < 2000 || first > 2100) return false;
    return v.length === 4 || Number(v.slice(5)) === (first + 1) % 100;
  }, 'escreva o ano (2025) ou a época (2020-21)');

/** O ano para ordenar: o ultimo da epoca ("2020-21" e 2021). */
export function hofSeasonYear(season: string): number {
  const first = Number(season.slice(0, 4));
  return season.length === 4 ? first : first + 1;
}

const imageSchema = z.object({ url: z.string(), alt: z.string() }).nullable();

// ---------------------------------------------------------------------------
// Site
// ---------------------------------------------------------------------------

export const publicHofWinnerSchema = z.object({
  /** A linha do premio (e a ela que se pede "e meu"). */
  id: z.string().uuid(),
  /** O nome a mostrar; `null` se o vencedor e hoje menor (so o nickname). */
  name: z.string().nullable(),
  /** O login da conta ligada; `null` se ainda nao esta ligado a uma conta. */
  nickname: z.string().nullable(),
  /** Pode-se dizer "e meu": ainda sem conta e sem pedido pendente. */
  claimable: z.boolean(),
});
export type PublicHofWinner = z.infer<typeof publicHofWinnerSchema>;

/** Um premio num ano, com todos os seus vencedores. */
export const publicHofAwardSchema = z.object({
  /** O id da primeira linha (para as chaves do React). */
  id: z.string().uuid(),
  name: z.string(),
  icon: hofIconSchema,
  reason: z.string(),
  photo: imageSchema,
  winners: z.array(publicHofWinnerSchema),
});
export type PublicHofAward = z.infer<typeof publicHofAwardSchema>;

export const publicHofSeasonSchema = z.object({
  label: z.string(),
  year: z.number().int(),
  awards: z.array(publicHofAwardSchema),
});
export type PublicHofSeason = z.infer<typeof publicHofSeasonSchema>;

/** `GET /api/hall-of-fame`: os anos com premios publicados, do mais recente para o mais antigo. */
export const publicHallOfFameSchema = z.object({
  seasons: z.array(publicHofSeasonSchema),
  /** Os nomes dos premios publicados, para o filtro "este premio ao longo dos anos". */
  prizes: z.array(z.object({ name: z.string(), icon: hofIconSchema })),
});
export type PublicHallOfFame = z.infer<typeof publicHallOfFameSchema>;

/**
 * `GET /api/hall-of-fame/membros/:nickname`: a pagina publica de um membro
 * (`growup.gg/membros/<nickname>`). Existe para quem a ativou (F-009, opt-in)
 * e para quem tem um premio ligado a conta. Carta sem numero de socio; foto so
 * com a pagina ativa e escolhida; um menor aparece so com o nickname (`name: null`).
 */
export const publicHofProfileSchema = z.object({
  nickname: z.string(),
  name: z.string().nullable(),
  /** A carta; `null` se a members-api nao respondeu (a pagina mostra so os premios). */
  card: z
    .object({
      level: z.enum(['user', 'member', 'associate']),
      arenaHours: z.number().int().nonnegative().nullable(),
      events: z.number().int().nonnegative().nullable(),
      years: z.number().int().nonnegative().nullable(),
      /** Streamer ativo: a carta fica roxa (ser streamer ja e publico). */
      streamer: z.boolean().default(false),
    })
    .nullable(),
  /** A pessoa ativou a sua pagina publica (F-009); sem isso, so ha premios. */
  page: z.boolean().default(false),
  /** A foto da carta, so se a pessoa ativou a pagina e escolheu mostra-la. */
  photoUrl: z.string().nullable().default(null),
  /** As redes que a pessoa escolheu mostrar; `url` `null` no Discord (nao ha perfil publico). */
  networks: z
    .array(
      z.object({
        network: z.enum(['discord', 'twitch', 'instagram', 'x', 'youtube']),
        handle: z.string(),
        url: z.string().nullable(),
      }),
    )
    .default([]),
  /** As equipas em que joga hoje (ativas), para a pagina de cada uma. */
  teams: z.array(z.object({ slug: z.string(), name: z.string(), game: z.string() })).default([]),
  /** Canal na Twitch, se e streamer ativo da Grow uP. */
  streamerChannel: z.string().nullable().default(null),
  /** Do mais recente para o mais antigo. */
  awards: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      icon: hofIconSchema,
      season: z.string(),
      reason: z.string(),
    }),
  ),
});
export type PublicHofProfile = z.infer<typeof publicHofProfileSchema>;

/** Um premio de um membro: carta (medalhas), conta e ficha no back-office. */
export const memberHofAwardSchema = z.object({
  awardId: z.string().uuid(),
  category: z.string(),
  icon: hofIconSchema,
  edition: z.string(),
  year: z.number().int(),
});
export type MemberHofAward = z.infer<typeof memberHofAwardSchema>;

/** `GET /api/me/hall-of-fame`: os meus premios e os pedidos ainda por confirmar. */
export const myHallOfFameSchema = z.object({
  awards: z.array(memberHofAwardSchema),
  pending: z.array(memberHofAwardSchema.extend({ winnerId: z.string().uuid(), name: z.string() })),
});
export type MyHallOfFame = z.infer<typeof myHallOfFameSchema>;

// ---------------------------------------------------------------------------
// Back-office
// ---------------------------------------------------------------------------

export const hofStatusSchema = z.enum(['draft', 'published']);
export type HofStatus = z.infer<typeof hofStatusSchema>;

export const adminHofPrizeSchema = z.object({
  id: z.string().uuid(),
  season: z.string(),
  year: z.number().int(),
  name: z.string(),
  icon: hofIconSchema,
  winnerName: z.string(),
  accountId: z.string().uuid().nullable(),
  /** O login da conta ligada (`null` se a conta foi apagada ou nao ha ligacao). */
  login: z.string().nullable(),
  /** Pedido "e meu" vindo do site, por confirmar. */
  claim: z.object({ accountId: z.string().uuid(), login: z.string(), at: z.string() }).nullable(),
  reason: z.string(),
  photoUrl: z.string().nullable(),
  photoAlt: z.string(),
  status: hofStatusSchema,
  sortOrder: z.number().int(),
  updatedAt: z.string(),
  /** Login de quem mexeu por ultimo. */
  updatedBy: z.string(),
});
export type AdminHofPrize = z.infer<typeof adminHofPrizeSchema>;

export const hofPrizeInputSchema = z
  .object({
    season: hofSeasonSchema,
    name: z.string().trim().min(1, 'escreva o nome do prémio').max(80),
    icon: hofIconSchema.default('medalha'),
    winnerName: z.string().trim().max(120).default(''),
    accountId: z.string().uuid().nullable().default(null),
    reason: z.string().trim().max(300).default(''),
    photoUrl: mediaSrcSchema.nullable().default(null),
    photoAlt: z.string().trim().max(300).default(''),
  })
  .refine((v) => v.winnerName !== '' || v.accountId !== null, {
    message: 'escreva o nome do vencedor ou escolha a conta',
    path: ['winnerName'],
  })
  .refine((v) => v.photoUrl === null || v.photoAlt !== '', {
    message: 'descreva a foto (para quem não a vê)',
    path: ['photoAlt'],
  });
export type HofPrizeInput = z.input<typeof hofPrizeInputSchema>;

export const hofOrderRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(2000),
});

export const hofAccountSchema = z.object({
  id: z.string().uuid(),
  login: z.string(),
  kind: z.enum(['standard', 'youth']),
});
export type HofAccount = z.infer<typeof hofAccountSchema>;

// ---------------------------------------------------------------------------
// Importacao do site antigo (RF-H8)
// ---------------------------------------------------------------------------

/** A pagina do WordPress com o Hall of Fame de 2012 a 2025. */
export const HOF_LEGACY_URL = 'https://growup.gg/about/hof/';

export const hofImportRequestSchema = z.object({
  /** `true`: so mostra o que ia importar. */
  dryRun: z.boolean().default(true),
});

export const hofImportResultSchema = z.object({
  seasons: z.array(
    z.object({
      label: z.string(),
      awards: z.array(z.object({ name: z.string(), winners: z.array(z.string()) })),
      /** Ja ha premios neste ano: fica como esta. */
      skipped: z.boolean(),
    }),
  ),
  /** Linhas que nao se perceberam (para rever a mao). */
  ignored: z.array(z.string()),
  imported: z.boolean(),
});
export type HofImportResult = z.infer<typeof hofImportResultSchema>;

/** O filtro da parede de membros (`?tipo=`). */
export const memberWallKindSchema = z.enum(['todos', 'membros', 'associados', 'streamers']);
export type MemberWallKind = z.infer<typeof memberWallKindSchema>;

export const memberWallQuerySchema = z.object({
  q: z.string().trim().max(60).optional(),
  tipo: memberWallKindSchema.default('todos'),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(96).default(48),
});

/**
 * `GET /api/hall-of-fame/membros`: a parede de membros (`growup.gg/membros`),
 * as cartas de quem ativou a pagina publica, por antiguidade.
 */
export const publicMemberWallSchema = z.object({
  cards: z.array(
    z.object({
      nickname: z.string(),
      level: z.enum(['user', 'member', 'associate']),
      years: z.number().int().nonnegative().nullable(),
      streamer: z.boolean(),
      photoUrl: z.string().nullable(),
      awards: z.array(z.object({ icon: hofIconSchema, title: z.string() })),
    }),
  ),
  total: z.number().int().nonnegative(),
});
export type PublicMemberWall = z.infer<typeof publicMemberWallSchema>;
